import React, { useEffect, useState } from "react";
import Layout from "../Component/Layout";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";

/* ===================== STYLES (MATCHING GRN DASHBOARD) ===================== */
const styles = {
  compactLabel: {
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "4px",
  },
  compactInput: {
    fontSize: "14px",
    height: "32px",
    padding: "4px 8px",
  },
  tableContainer: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 12,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  erpTable: {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
  },
  erpTh: {
    background: "#f8fafc",
    fontSize: 13,
    fontWeight: 600,
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1e293b",
    whiteSpace: "nowrap",
  },
  erpTd: {
    fontSize: 13,
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155",
    verticalAlign: "middle",
  },
  filterCard: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    marginBottom: 18,
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6,
    color: "#334155",
  },
  filterSelect: {
    height: 36,
    fontSize: 13,
  },
  pageTitleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 2,
  },
  pageSubtitle: {
    fontSize: 13,
    color: "#64748b",
  },
};

/* ===================== COMPONENT ===================== */
const ConfirmationDashboard = () => {
  const [productionOrders, setProductionOrders] = useState([]);
  const [batchList, setBatchList] = useState([]);
  const [batches, setBatches] = useState([]);

  const [filters, setFilters] = useState({
    productionNo: "",
    batchNo: "",
  });

  useEffect(() => {
    loadProductionOrders();
    fetchTableData({ productionNo: "", batchNo: "" });
  }, []);

  /* =========================
      LOAD DROPDOWNS
  ========================= */
  const loadProductionOrders = async () => {
    try {
      const res = await axios.get(
        "https://master.erp.sbeecables.com/api/confirmationdashboard/production-orders"
      );
      setProductionOrders(res.data);
    } catch (error) {
      console.error("Error loading production orders", error);
    }
  };

  const loadBatches = async (productionNo) => {
    try {
      const res = await axios.get(
        `https://master.erp.sbeecables.com/api/confirmationdashboard/batches-by-production/${productionNo}`
      );
      setBatchList(res.data);
    } catch (error) {
      console.error("Error loading batches", error);
    }
  };

  /* =========================
      FETCH TABLE
  ========================= */
  const fetchTableData = async (updatedFilters) => {
    try {
      const res = await axios.post(
        "https://master.erp.sbeecables.com/api/confirmationdashboard/batches-filtered",
        updatedFilters
      );
      if (res.data.success) {
        setBatches(res.data.batches);
      }
    } catch (error) {
      console.error("Error fetching table data", error);
    }
  };

  /* =========================
      HANDLERS
  ========================= */
  const handleProductionChange = async (value) => {
    const newFilters = { productionNo: value, batchNo: "" };
    setFilters(newFilters);
    setBatchList([]);

    if (value) {
      await loadBatches(value);
    }
    fetchTableData(newFilters);
  };

  const handleBatchChange = (value) => {
    const newFilters = { ...filters, batchNo: value };
    setFilters(newFilters);
    fetchTableData(newFilters);
  };

  const formatDate = (d) =>
    d ? new Date(d).toISOString().split("T")[0] : "";

  /* =========================
      UI
  ========================= */
  return (
    <Layout>
      {/* ===================== HEADER ===================== */}
      <div style={styles.pageTitleBar}>
        <div>
          <h2 style={styles.pageTitle}>Confirmation Dashboard</h2>
          <div style={styles.pageSubtitle}>
            Batch confirmation & production tracking
          </div>
        </div>
      </div>

      {/* ===================== FILTERS ===================== */}
      <div style={styles.filterCard}>
        <div className="row g-4">
          <div className="col-md-3">
            <label style={styles.filterLabel}>Production Order</label>
            <select
              className="form-control"
              style={styles.filterSelect}
              value={filters.productionNo}
              onChange={(e) => handleProductionChange(e.target.value)}
            >
              <option value="">Select Production Order</option>
              {productionOrders.map((p, i) => (
                <option key={i} value={p.production_order_number}>
                  {p.production_order_number}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label style={styles.filterLabel}>Batch Number</label>
            <select
              className="form-control"
              style={styles.filterSelect}
              value={filters.batchNo}
              onChange={(e) => handleBatchChange(e.target.value)}
              disabled={!filters.productionNo}
            >
              <option value="">Select Batch</option>
              {batchList.map((b, i) => (
                <option key={i} value={b.batch_no}>
                  {b.batch_no}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ===================== TABLE ===================== */}
      <div style={styles.tableContainer}>
        <table className="table" style={styles.erpTable}>
          <thead>
            <tr>
              <th style={styles.erpTh}>Production Order</th>
              <th style={styles.erpTh}>Product Code</th>
              <th style={styles.erpTh}>Batch Number</th>
              <th style={{ ...styles.erpTh, textAlign: "right" }}>Quantity</th>
              <th style={{ ...styles.erpTh, textAlign: "right" }}>Delivered</th>
              <th style={styles.erpTh}>Start Date</th>
              <th style={styles.erpTh}>End Date</th>
            </tr>
          </thead>
          <tbody>
            {batches.length === 0 ? (
              <tr>
                <td
                  colSpan="7"
                  style={{ ...styles.erpTd, textAlign: "center" }}
                >
                  No records found
                </td>
              </tr>
            ) : (
              batches.map((b) => (
                <tr key={b.id || b.batch_no}>
                  <td style={styles.erpTd}>{b.production_order_number}</td>
                  <td style={styles.erpTd}>{b.header_product_code}</td>
                  <td style={styles.erpTd}>{b.batch_no}</td>
                  <td style={{ ...styles.erpTd, textAlign: "right" }}>
                    {b.actualQty}
                  </td>
                  <td style={{ ...styles.erpTd, textAlign: "right" }}>
                    {b.deliveredActual}
                  </td>
                  <td style={styles.erpTd}>{formatDate(b.startDate)}</td>
                  <td style={styles.erpTd}>{formatDate(b.endDate)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ===================== FOOTER ===================== */}
      <div className="d-flex justify-content-between mt-2">
        <div style={{ fontSize: 12, color: "#64748b" }}>
          Showing {batches.length} results
        </div>
        <div>
          <button className="btn btn-outline-secondary btn-sm me-1">
            ← Prev
          </button>
          <button className="btn btn-outline-secondary btn-sm">
            Next →
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default ConfirmationDashboard;